import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth.tsx';
import { useAccountStatus } from './useAccountStatus';
import { getWorkerUrl } from '@/lib/config';

export interface CreditBalance {
  plan_credits: number;
  purchased_credits: number;
  total_credits: number;
}

export interface CreditTransaction {
  id: string;
  account_id: string;
  amount: number;
  balance_after: number;
  type: 'plan_allocation' | 'purchase' | 'consumption' | 'refund' | 'adjustment';
  category: 'content_generation' | 'image_processing' | 'video_processing' | 'other' | null;
  description: string;
  metadata: any;
  created_at: string;
}

export interface CreditCosts {
  costs: any;
  packages: Array<{
    credits: number;
    price: number;
    label: string;
    popular: boolean;
  }>;
}

export interface UsageSummary {
  month: {
    totalConsumed: number;
    byCategory: Record<string, number>;
    byDay: Array<{
      date: string;
      amount: number;
    }>;
  };
}

export function useCredits() {
  const authContext = useAuth();
  const { session, loading: authLoading } = authContext;
  const { status: accountStatus } = useAccountStatus();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [costs, setCosts] = useState<CreditCosts | null>(null);

  // Debug logging for auth context
  useEffect(() => {
    console.log('[useCredits] Auth context changed:', {
      authLoading,
      hasSession: !!session,
      sessionId: session?.user?.id,
      hasAccountStatus: !!accountStatus,
      accountId: accountStatus?.personalAccount?.id,
      contextKeys: Object.keys(authContext)
    });
  }, [authContext, authLoading, session, accountStatus]);

  const fetchBalance = useCallback(async () => {
    try {
      // Get session and account directly, like PlanSelector does
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        console.log('[useCredits] No session found');
        setLoading(false);
        return;
      }

      // Get account ID directly
      const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null };
      if (!account?.account_id) {
        console.log('[useCredits] No account found');
        setLoading(false);
        return;
      }

      console.log('[useCredits] Fetching balance with session:', currentSession.user.id, 'account:', account.account_id);

      const workerUrl = getWorkerUrl()
      const response = await fetch(`${workerUrl}/api/credits/balance`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch credit balance');
      }

      const data = await response.json();
      console.log('[useCredits] Balance fetched:', data);
      setBalance(data.balance);
      setUsage(data.usage); // Set usage data from the response
      setError(null);
    } catch (err) {
      console.error('Error fetching credit balance:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCosts = useCallback(async () => {
    try {
      // Get session directly to ensure we have the latest
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        console.log('[useCredits] No session found for costs');
        return;
      }

      console.log('[useCredits] Fetching costs with session:', currentSession.user.id);

      const workerUrl = getWorkerUrl()
      const response = await fetch(`${workerUrl}/api/credits/costs`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch credit costs');
      }

      const data = await response.json();
      setCosts(data);
    } catch (err) {
      console.error('Error fetching credit costs:', err);
    }
  }, []);

  const fetchTransactions = useCallback(async (options?: {
    limit?: number;
    offset?: number;
    type?: string;
    category?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        return { transactions: [], total: 0 };
      }

      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());
      if (options?.type) params.append('type', options.type);
      if (options?.category) params.append('category', options.category);
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);

      const workerUrl = getWorkerUrl();
      const response = await fetch(
        `${workerUrl}/api/credits/transactions?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${currentSession.access_token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch transactions');
      }

      return await response.json();
    } catch (err) {
      console.error('Error fetching transactions:', err);
      return { transactions: [], total: 0 };
    }
  }, []);

  const purchaseCredits = useCallback(async (credits: number) => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession) {
      throw new Error('Not authenticated');
    }

    const workerUrl = getWorkerUrl();
    const response = await fetch(`${workerUrl}/api/credits/purchase`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentSession.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ credits })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create purchase');
    }

    const data = await response.json();
    return data.checkout_url;
  }, []);

  const getUsageSummary = useCallback(async (period: 'day' | 'week' | 'month' = 'month') => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        return null;
      }

      const workerUrl = getWorkerUrl();
      const response = await fetch(
        `${workerUrl}/api/credits/usage?period=${period}`,
        {
          headers: {
            'Authorization': `Bearer ${currentSession.access_token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch usage summary');
      }

      const data = await response.json();
      return data.summary;
    } catch (err) {
      console.error('Error fetching usage summary:', err);
      return null;
    }
  }, []);

  // Initial fetch - much simpler approach, like PlanSelector
  useEffect(() => {
    console.log('[useCredits] Hook initialized, attempting to fetch data...');

    // Try to fetch both costs and balance
    // Each function handles its own session/account checks
    fetchCosts();
    fetchBalance();
  }, []); // No dependencies - just fetch once on mount

  // Set up real-time subscription for balance updates
  useEffect(() => {
    let channel: any = null;

    const setupSubscription = async () => {
      try {
        // Get account ID directly
        const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null };
        if (!account?.account_id) {
          console.log('[useCredits] No account ID for real-time subscription');
          return;
        }

        console.log('[useCredits] Setting up real-time subscription for account:', account.account_id);

        channel = supabase
          .channel(`credit-balance-${account.account_id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'basejump',
              table: 'credit_balances',
              filter: `account_id=eq.${account.account_id}`
            },
            () => {
              // Refresh balance when it changes
              fetchBalance();
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'basejump',
              table: 'credit_transactions',
              filter: `account_id=eq.${account.account_id}`
            },
            () => {
              // Refresh balance when new transaction is added
              fetchBalance();
            }
          )
          .subscribe();
      } catch (error) {
        console.error('[useCredits] Error setting up real-time subscription:', error);
      }
    };

    setupSubscription();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [fetchBalance]);

  return {
    balance,
    usage,
    loading,
    error,
    costs,
    fetchBalance,
    fetchTransactions,
    purchaseCredits,
    getUsageSummary
  };
}